/**
 * L'écran de pointage de la garderie : la liste d'un jour, sur un téléphone.
 *
 * Une école, une date (aujourd'hui par défaut, une date passée pour
 * réencoder), une plage (à l'ouverture, celle qu'un scan poserait à cette
 * heure — SCAN-QR § 2.1 — ; choisie, elle ne change plus toute seule), un
 * groupe (tous, maternelles, primaires), une recherche, et un ✓ par enfant :
 * présent, ou rien. Chaque geste s'enregistre aussitôt dans
 * `garderie_presences` ; la liste se relit toutes les quinze secondes et au
 * retour sur l'écran, pour que plusieurs personnes voient les mêmes ✓.
 *
 * Deux sources, un écran (`SourceDuPointage`) : le lien de l'animateur, sans
 * compte (`/garderie/:jeton`), et Gestion, où le tableau de bord mène
 * directement à la liste du jour d'une école (02/11/2026). La base juge chaque
 * geste. Un doigt posé pour faire défiler ne coche rien, un double appui ne
 * décoche pas (`useAppuiFranc`). Ici, rien d'argent.
 *
 * Le scan des étiquettes (SCAN-QR, lot 1) : « Scanner » ouvre la feuille de scan
 * (`scan/EcranDeScan.tsx`, chargée à la demande) quand la source sait scanner, sur la
 * liste d'aujourd'hui, mois non facturé ; la file des scans (`useFileDeScans`) vit ici,
 * pour continuer de partir quand la feuille est fermée. Et les défauts du § 7 corrigés :
 * bandeau d'erreur dans l'en-tête collant, erreurs PostgREST lues (`messageDErreur`), ✓
 * réessayé quand le réseau manque, relectures numérotées, date qui revient au jour même,
 * bandeau « un ✓ ici compte … » quand l'heure a tourné (`regles-de-l-ecran.ts`).
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, CalendarClock, Check, ChevronLeft, ChevronRight, Clock, Loader2, Lock, Phone, RefreshCw, ScanLine, Search, X } from 'lucide-react';
import {
  ageEnAnnees,
  appliquerPointage,
  aujourdHuiBruxelles,
  compterParGroupe,
  compterPresents,
  dateEnClair,
  classesDeLaListe,
  estErreurDeReseau,
  filtrerEnfants,
  garderLesHabitudes,
  SANS_CLASSE,
  trierParHabitude,
  initiales,
  lendemain,
  messageDErreur,
  messageDeRefus,
  debutDuJour,
  finDuJour,
  heureEnClair,
  ouvertureDuPointage,
  plageParDefaut,
  plagePasCommencee,
  plagesDuJour,
  TOLERANCE_HORLOGE_MIN,
  veille,
  type EnfantDuLien,
  type FicheGarderie,
  type FiltreDeGroupe,
  type LecturePointage,
  type PlageDuLien,
  type SourceDuPointage,
} from '../../lib/garderie/pointage';
import { dateEtHeureBruxelles, momentCorrige, type ResultatDuScan } from '../../lib/garderie/scan';
import { useAppuiFranc } from './useAppuiFranc';
import {
  appliquerLesScans,
  avecReessais,
  bandeauDeLaPlage,
  CompteurDeRelectures,
  dateAuRetour,
  dateCourte,
  lotAChangeLaListe,
  peutScanner,
} from './regles-de-l-ecran';
import { useFileDeScans, type MesureDHorloge } from './scan/useFileDeScans';
import { ouvrirLeSon } from './scan/sons';
import { bandeauDesOrphelines, bandeauDuLienMort, texteEnAttente } from './scan/retours';

/* Le décodeur (Worker et WebAssembly) ne se charge qu'avec la feuille de scan. */
const EcranDeScan = lazy(() => import('./scan/EcranDeScan'));

interface Props {
  source: SourceDuPointage;
  /** Sous le nom de l'école : le prénom de l'animateur (le lien), ou le compte qui pointe (Gestion). */
  sousTitre?: ReactNode;
  /** Le lien demande son code. */
  onCodeRequis?: () => void;
  /** Le code de la plage à ouvrir (le tableau de bord) ; sinon celle de l'heure. */
  plageInitiale?: string | null;
  /** `AAAA-MM-JJ` ; aujourd'hui sinon. */
  dateInitiale?: string | null;
  /** Pleine page (le lien) ou dans la mise en page de Gestion. */
  pleinePage?: boolean;
  /** La classe `top-*` de l'en-tête collant : sous la barre de Gestion, plus bas. */
  hautCollant?: string;
  /** Le prénom noté avec chaque scan (le lien) ; `null` depuis Gestion, où la base note le compte. */
  pointePar?: string | null;
}

const GROUPES: { cle: FiltreDeGroupe; libelle: string }[] = [
  { cle: 'tous', libelle: 'Tous' },
  { cle: 'maternelle', libelle: 'Maternelles' },
  { cle: 'primaire', libelle: 'Primaires' },
];

export function EcranDePointage({
  source,
  sousTitre,
  onCodeRequis,
  plageInitiale = null,
  dateInitiale = null,
  pleinePage = true,
  hautCollant = 'top-0',
  pointePar = null,
}: Props) {
  const aujourdHui = aujourdHuiBruxelles();
  const [date, setDate] = useState<string>(() => (dateInitiale && dateInitiale <= aujourdHui ? dateInitiale : aujourdHui));
  /* Une date choisie à la main (réencodage) reste au retour sur l'écran ; sinon elle revient au jour même. */
  const dateALaMain = useRef(date !== aujourdHui);
  const dateRef = useRef(date);
  dateRef.current = date;
  const [lecture, setLecture] = useState<LecturePointage | null>(null);
  const [plageChoisie, setPlageChoisie] = useState<string | null>(null);
  const [initialeAppliquee, setInitialeAppliquee] = useState(!plageInitiale);
  const [groupe, setGroupe] = useState<FiltreDeGroupe>(() => {
    try {
      const g = localStorage.getItem(`garderie-groupe-${source.cle}`);
      return g === 'maternelle' || g === 'primaire' ? g : 'tous';
    } catch {
      return 'tous';
    }
  });
  const [recherche, setRecherche] = useState('');
  /* La classe : pour trouver un enfant, pas mémorisée (une liste rouverte montre toute l'école). */
  const [classe, setClasse] = useState('');
  const [chargement, setChargement] = useState(true);
  const [rafraichissement, setRafraichissement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<Set<string>>(new Set());
  const [fiche, setFiche] = useState<{ enfant: EnfantDuLien; contenu: FicheGarderie | null; erreur: string | null } | null>(null);
  const [horloge, setHorloge] = useState<MesureDHorloge | null>(null);
  const [scanOuvert, setScanOuvert] = useState(false);
  const [instant, setInstant] = useState(() => Date.now());
  const relectures = useRef(new CompteurDeRelectures());
  const { suivi, geste } = useAppuiFranc();

  const relire = useCallback(
    async (silencieux: boolean) => {
      const n = relectures.current.demander();
      if (!silencieux) setChargement(true);
      else setRafraichissement(true);
      try {
        const envoyeLe = Date.now();
        const l = await source.lire(date);
        const recuLe = Date.now();
        /* Une réponse plus ancienne que la dernière demandée (ou qu'un geste) est ignorée. */
        if (!relectures.current.estAJour(n)) return;
        if (l.codeRequis) {
          onCodeRequis?.();
          return;
        }
        /* La relecture rafraîchit les ✓ ; l'ordre de la liste reste celui de la première lecture. */
        setLecture((avant) => garderLesHabitudes(avant, l));
        setErreur(null);
        if (l.maintenant) setHorloge({ maintenant: l.maintenant, envoyeLe, recuLe });
      } catch (e) {
        if (relectures.current.estAJour(n)) setErreur(messageDErreur(e));
      } finally {
        if (relectures.current.estLaDerniere(n)) {
          setChargement(false);
          setRafraichissement(false);
        }
      }
    },
    [source, date, onCodeRequis],
  );
  const relireRef = useRef(relire);
  relireRef.current = relire;

  /* Démonté : les relectures encore en route ne s'appliquent plus (ni « code requis » d'un ancien code). */
  useEffect(() => {
    const compteur = relectures.current;
    return () => compteur.invalider();
  }, []);

  useEffect(() => {
    void relire(false);
  }, [relire]);

  /* La date qui revient au jour même, au retour sur l'écran et quand minuit passe. */
  const revenirAuJour = useCallback(() => {
    const d = dateAuRetour(dateRef.current, dateALaMain.current, aujourdHuiBruxelles());
    if (d !== dateRef.current) {
      dateALaMain.current = false;
      setPlageChoisie(null);
      setDate(d);
    }
  }, []);

  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState !== 'visible') return;
      const avant = dateRef.current;
      revenirAuJour();
      if (dateRef.current === avant) void relire(true);
    };
    document.addEventListener('visibilitychange', auRetour);
    const minuterie = window.setInterval(() => void relire(true), 15_000);
    return () => {
      document.removeEventListener('visibilitychange', auRetour);
      window.clearInterval(minuterie);
    };
  }, [relire, revenirAuJour]);

  /* L'heure des bandeaux, toutes les dix secondes ; et minuit. */
  useEffect(() => {
    const t = window.setInterval(() => {
      setInstant(Date.now());
      if (document.visibilityState === 'visible') revenirAuJour();
    }, 10_000);
    return () => window.clearInterval(t);
  }, [revenirAuJour]);

  useEffect(() => {
    try {
      localStorage.setItem(`garderie-groupe-${source.cle}`, groupe);
    } catch {
      /* navigation privée */
    }
  }, [groupe, source.cle]);

  /* La file des scans : elle part même feuille fermée ; après chaque lot, la liste se relit. */
  const apresUnLot = useCallback((traites: ResultatDuScan[]) => {
    setLecture((l) => (l ? appliquerLesScans(l, traites) : l));
    /* Invalider seulement pour relancer aussitôt : sinon une relecture en route (une autre date, minuit
       passé) serait perdue sans être remplacée, et la liste resterait sur la veille. */
    if (lotAChangeLaListe(traites)) {
      relectures.current.invalider();
      void relireRef.current(true);
    }
  }, []);
  const file = useFileDeScans(source, lecture, { horloge, onLot: apresUnLot });
  const lienMort = bandeauDuLienMort(file.lienMort, file.enAttente);
  const orphelines = useMemo(() => bandeauDesOrphelines(file.orphelines), [file.orphelines]);

  const changerDate = (d: string) => {
    dateALaMain.current = d !== aujourdHuiBruxelles();
    setDate(d);
  };

  const { heure: heureDuMoment } = dateEtHeureBruxelles(momentCorrige(instant, file.ecartMs));
  const duJour = useMemo(() => (lecture ? plagesDuJour(lecture.plages, lecture.date) : []), [lecture]);
  /* La plage demandée par le tableau de bord, une seule fois ; puis celle qu'on choisit, ou celle de l'heure. */
  const initiale = !initialeAppliquee && plageInitiale ? (duJour.find((p) => p.code === plageInitiale)?.id ?? null) : null;
  const plageId = lecture ? plageParDefaut(lecture, duJour, plageChoisie ?? initiale, heureDuMoment) : null;
  /* Figer la plage ouverte : à 16 h pile, la liste ne passe pas d'ATL1 à ATL2 sous les doigts. */
  useEffect(() => {
    /* Pas sur la lecture d'une autre date (le jour vient de changer) : on attend la bonne. */
    if (!lecture || lecture.date !== date) return;
    if (plageId !== plageChoisie) setPlageChoisie(plageId);
    if (!initialeAppliquee) setInitialeAppliquee(true);
  }, [lecture, date, plageId, plageChoisie, initialeAppliquee]);

  const plage = duJour.find((p) => p.id === plageId) ?? null;
  const bandeau = bandeauDeLaPlage(lecture, plageId, heureDuMoment);
  /* Le jour même, une plage ne se pointe qu'une demi-heure avant son début (04/11/2026) : à l'heure
     corrigée sur celle du serveur ; la case ne se bloque qu'avec une tolérance, la base tranche. */
  const pasCommencee = (p: PlageDuLien) => !!lecture && plagePasCommencee(p, lecture.date, lecture.aujourdHui, heureDuMoment);
  const plageFermee = plage !== null && pasCommencee(plage);
  const plageBloquee =
    plage !== null && !!lecture && plagePasCommencee(plage, lecture.date, lecture.aujourdHui, heureDuMoment, TOLERANCE_HORLOGE_MIN);
  const debutDeLaPlage = plage && lecture ? debutDuJour(plage, lecture.date) : null;
  /* Les habitués de la plage ouverte d'abord, puis l'ordre alphabétique. */
  const choixDeClasses = useMemo(() => (lecture ? classesDeLaListe(lecture.enfants, groupe) : { classes: [], sansClasse: 0 }), [lecture, groupe]);
  /* Une classe qui n'est plus dans le groupe choisi (ou plus dans la liste) ne filtre plus rien en silence. */
  const classeActive =
    classe === SANS_CLASSE ? (choixDeClasses.sansClasse > 0 ? classe : '') : choixDeClasses.classes.some((c) => c.classe === classe) ? classe : '';
  const visibles = useMemo(
    () => (lecture ? trierParHabitude(filtrerEnfants(lecture.enfants, groupe, recherche, classeActive), plageId) : []),
    [lecture, groupe, recherche, classeActive, plageId],
  );
  const comptes = useMemo(() => (lecture ? compterParGroupe(lecture.enfants) : { tous: 0, maternelle: 0, primaire: 0 }), [lecture]);
  const lectureSeule = lecture?.moisFacture ?? false;
  const scanPossible = peutScanner(source, lecture, aujourdHui);

  const basculer = async (enfant: EnfantDuLien) => {
    if (!lecture || !plageId || lectureSeule || enCours.has(enfant.id)) return;
    /* La liste affichée n'est pas encore celle de la date choisie (elle se relit) : aucun ✓ sur l'autre date. */
    if (lecture.date !== date) return;
    const present = !enfant.presences.includes(plageId);
    /* Une plage du jour qui n'a pas commencé : on retire un ✓ posé par erreur, on n'en pose pas. */
    if (present && plageBloquee && debutDeLaPlage) {
      setErreur(messageDeRefus(`plage_pas_commencee:${debutDeLaPlage}`));
      return;
    }
    setEnCours((s) => new Set(s).add(enfant.id));
    setLecture((l) => (l ? { ...l, enfants: appliquerPointage(l.enfants, enfant.id, plageId, present) } : l));
    /* Une relecture partie avant le geste ne doit pas l'effacer. */
    relectures.current.invalider();
    try {
      /* Le réseau manque : le geste est réessayé (2 s, puis 5 s) ; la base est idempotente. */
      const r = await avecReessais(() => source.pointer(enfant.id, lecture.date, plageId, present), estErreurDeReseau);
      if (r.codeRequis) {
        setLecture((l) => (l ? { ...l, enfants: appliquerPointage(l.enfants, enfant.id, plageId, !present) } : l));
        setErreur(`${present ? 'Le ✓' : 'Le retrait'} de ${enfant.prenom} n’est pas enregistré : retapez le code.`);
        onCodeRequis?.();
        return;
      }
      setLecture((l) => (l ? { ...l, enfants: appliquerPointage(l.enfants, enfant.id, plageId, r.present) } : l));
    } catch (e) {
      setLecture((l) => (l ? { ...l, enfants: appliquerPointage(l.enfants, enfant.id, plageId, !present) } : l));
      setErreur(`${present ? 'Le ✓' : 'Le retrait'} de ${enfant.prenom} n’est pas enregistré. ${messageDErreur(e)}`);
    } finally {
      relectures.current.invalider();
      setEnCours((s) => {
        const n = new Set(s);
        n.delete(enfant.id);
        return n;
      });
    }
  };

  const ouvrirFiche = async (enfant: EnfantDuLien) => {
    if (!source.lireFiche) return;
    setFiche({ enfant, contenu: null, erreur: null });
    try {
      const f = await source.lireFiche(enfant.id);
      if (f.codeRequis) {
        onCodeRequis?.();
        return;
      }
      setFiche((x) => (x && x.enfant.id === enfant.id ? { ...x, contenu: f } : x));
    } catch (e) {
      setFiche((x) => (x && x.enfant.id === enfant.id ? { ...x, erreur: messageDErreur(e) } : x));
    }
  };

  const cadre = pleinePage ? 'min-h-screen bg-gray-50 pb-24 text-gray-900' : '-mx-3 bg-gray-50 pb-10 text-gray-900 sm:mx-0 sm:rounded-2xl sm:ring-1 sm:ring-gray-200';

  if (chargement && !lecture) {
    return (
      <div className={`flex items-center justify-center ${pleinePage ? 'min-h-screen bg-gray-50' : 'py-16'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!lecture) {
    return (
      <div className={pleinePage ? 'min-h-screen bg-gray-50 p-4' : 'py-4'}>
        <div className="mx-auto mt-16 max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
          <p className="mt-3 text-gray-800">{erreur ?? 'Impossible de lire la liste.'}</p>
          {/* La liste ne se lit pas (lien remplacé, expiré…) : les scans qui attendent ici ne sont jamais tus. */}
          {file.enAttente > 0 && (
            <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-red-800">
              {file.enAttente} scan{file.enAttente > 1 ? 's attendent' : ' attend'} sur ce téléphone. Si ce lien a été remplacé, ouvrez ici le
              nouveau lien de l’école : {file.enAttente > 1 ? 'ils partiront' : 'il partira'} avec lui.
            </p>
          )}
          {orphelines && (
            <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm text-amber-900">
              {orphelines.texte} <span className="text-xs">{orphelines.detail}</span>
            </p>
          )}
          <button type="button" onClick={() => void relire(false)} className="mt-4 rounded-xl bg-primary-600 px-4 py-2 font-semibold text-white">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cadre}>
      <div className={`sticky ${hautCollant} z-10 border-b border-gray-200 bg-white/95 backdrop-blur ${pleinePage ? '' : 'sm:rounded-t-2xl'}`}>
        <div className="mx-auto max-w-2xl px-3 pt-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold">{lecture.ecole.nom}</h1>
              {sousTitre && <div className="text-xs text-gray-500">{sousTitre}</div>}
            </div>
            <button
              type="button"
              onClick={() => void relire(true)}
              disabled={rafraichissement}
              className="rounded-full p-2 text-gray-500 active:bg-gray-100"
              aria-label="Relire la liste"
            >
              <RefreshCw className={`h-5 w-5 ${rafraichissement ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* La date */}
          <div className="mt-2 flex items-center justify-between gap-2">
            <button type="button" onClick={() => changerDate(veille(date))} className="rounded-full p-2 active:bg-gray-100" aria-label="La veille">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <label className="relative flex-1 text-center">
              <span className="block text-sm font-semibold first-letter:uppercase">{dateEnClair(lecture.date)}</span>
              <input
                type="date"
                value={date}
                max={aujourdHui}
                onChange={(e) => e.target.value && changerDate(e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="Choisir une date"
              />
            </label>
            <button
              type="button"
              onClick={() => changerDate(lendemain(date))}
              disabled={date >= aujourdHui}
              className="rounded-full p-2 active:bg-gray-100 disabled:opacity-30"
              aria-label="Le lendemain"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Une autre date que le jour même : toujours dit, jamais oublié. */}
          {date !== aujourdHui && (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-lg bg-amber-100 px-3 py-1.5 text-sm text-amber-900">
              <span className="flex items-center gap-1 font-semibold">
                <CalendarClock className="h-4 w-4" /> Vous pointez le {dateCourte(date)}
              </span>
              <button
                type="button"
                onClick={() => {
                  dateALaMain.current = false;
                  setPlageChoisie(null);
                  setDate(aujourdHuiBruxelles());
                }}
                className="rounded-lg bg-white px-2 py-1 font-semibold text-amber-900 ring-1 ring-amber-300"
              >
                Revenir à aujourd’hui
              </button>
            </div>
          )}

          {lecture.ferie && <p className="mt-1 rounded-lg bg-amber-50 px-3 py-1 text-center text-xs text-amber-800">Jour férié : {lecture.ferie}</p>}
          {lectureSeule && (
            <p className="mt-1 flex items-center justify-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-center text-xs text-gray-700">
              <Lock className="h-3 w-3" /> Mois facturé : lecture seule
            </p>
          )}

          {plageFermee && plage && debutDeLaPlage && (
            <p className="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-center text-sm text-amber-900 ring-1 ring-amber-200">
              <span className="font-semibold">{plage.libelle}</span> commence à {heureEnClair(debutDeLaPlage)} : on la pointe à partir de{' '}
              {heureEnClair(ouvertureDuPointage(debutDeLaPlage))}. Un ✓ posé par erreur peut être retiré.
            </p>
          )}

          {/* L'heure a tourné : la liste compte toujours sa plage, on le dit. */}
          {bandeau && (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm text-blue-900">
              <span>
                Il est {bandeau.heure} : un ✓ ici compte <span className="font-bold">{bandeau.ici.code}</span>
              </span>
              <button
                type="button"
                {...suivi}
                onClick={() => {
                  if (geste(`passer|${bandeau.regle.id}`)) setPlageChoisie(bandeau.regle.id);
                }}
                className="flex-shrink-0 touch-manipulation select-none rounded-lg bg-blue-600 px-2 py-1 font-semibold text-white"
              >
                Passer en {bandeau.regle.code}
              </button>
            </div>
          )}

          {/* Les erreurs : en haut, toujours visibles (SCAN-QR § 7, 1). */}
          {erreur && (
            <div className="mt-1 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span className="flex-1">{erreur}</span>
              <button type="button" onClick={() => setErreur(null)} aria-label="Fermer">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Scanner les étiquettes, et la file qui attend. */}
          {scanPossible && (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  ouvrirLeSon(); // dans le geste : sans cela, pas de son sur iPhone
                  setScanOuvert(true);
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-600 py-3 text-lg font-bold text-white active:bg-primary-700"
              >
                <ScanLine className="h-6 w-6" /> Scanner
              </button>
              {(file.enAttente > 0 || !file.lisible) && (
                <span
                  className={`flex items-center gap-1 rounded-xl px-3 py-3 text-sm font-semibold ${file.codeRequis || file.lienMort || !file.lisible ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}
                >
                  <Clock className="h-4 w-4" /> {file.lisible ? `${file.enAttente} en attente` : 'file illisible'}
                </span>
              )}
            </div>
          )}
          {!scanPossible && file.enAttente > 0 && (
            <p className="mt-2 flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
              <Clock className="h-4 w-4" /> {file.enAttente} scan{file.enAttente > 1 ? 's' : ''} en attente d’envoi
            </p>
          )}
          {/* Ce qui attend sans pouvoir partir : toujours visible, hors de la feuille de scan (SCAN-QR § 5.2). */}
          {!file.lisible && (
            <p className="mt-1 rounded-lg bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-800" role="alert">
              {texteEnAttente(file.enAttente, false)}
            </p>
          )}
          {lienMort && (
            <p className="mt-1 rounded-lg bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-800" role="alert">
              {lienMort}
            </p>
          )}
          {orphelines && (
            <div className="mt-1 rounded-lg bg-amber-50 px-3 py-1.5 text-sm text-amber-900" role="status">
              <p className="font-semibold">{orphelines.texte}</p>
              <p className="text-xs">{orphelines.detail}</p>
            </div>
          )}
          {file.codeRequis && !scanOuvert && (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-800">
              <span className="font-semibold">
                {file.enAttente} scan{file.enAttente > 1 ? 's attendent' : ' attend'} : retapez le code
              </span>
              {onCodeRequis && (
                <button type="button" onClick={onCodeRequis} className="rounded-lg bg-red-600 px-2 py-1 font-semibold text-white">
                  Retaper
                </button>
              )}
            </div>
          )}

          {/* Les plages */}
          {duJour.length === 0 ? (
            <p className="mt-2 pb-3 text-center text-sm text-gray-500">Pas de garderie ce jour-là.</p>
          ) : (
            <div className="-mx-3 mt-2 flex gap-2 overflow-x-auto px-3 pb-3">
              {duJour.map((p) => {
                const actif = p.id === plageId;
                const n = compterPresents(lecture.enfants, p.id);
                const fermee = pasCommencee(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    {...suivi}
                    onClick={() => {
                      if (geste(`plage|${p.id}`)) setPlageChoisie(p.id);
                    }}
                    className={`flex-shrink-0 touch-manipulation select-none rounded-xl px-3 py-2 text-left shadow-sm ${
                      actif
                        ? fermee
                          ? 'bg-gray-600 text-white'
                          : 'bg-primary-600 text-white'
                        : fermee
                          ? 'bg-gray-100 text-gray-600 ring-1 ring-dashed ring-gray-300'
                          : 'bg-white ring-1 ring-gray-200'
                    }`}
                  >
                    <span className="block text-sm font-semibold">{p.libelle}</span>
                    {/* L'heure en évidence : on voit d'un coup d'œil quel moment on pointe (04/11/2026). */}
                    <span className={`block text-sm ${actif ? 'text-white' : fermee ? 'text-gray-600' : 'text-gray-900'}`}>
                      {fermee ? (
                        <>
                          commence à <span className="font-bold">{heureEnClair(debutDuJour(p, lecture.date))}</span>
                          {/* Un ✓ sur une plage pas commencée est forcément à vérifier : on le montre. */}
                          {n > 0 && <span className={actif ? 'font-semibold text-white' : 'font-semibold text-amber-700'}> · {n} ✓ à vérifier</span>}
                        </>
                      ) : (
                        <>
                          <span className="font-bold">
                            {heureEnClair(debutDuJour(p, lecture.date))}–{heureEnClair(finDuJour(p, lecture.date))}
                          </span>
                          <span className={actif ? 'text-white/80' : 'text-gray-500'}> · {n} ✓</span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-3">
        {/* Les groupes et la recherche */}
        <div className="mt-3 flex gap-2">
          {GROUPES.map((g) => (
            <button
              key={g.cle}
              type="button"
              onClick={() => {
                setGroupe(g.cle);
                setClasse('');
              }}
              className={`flex-1 rounded-xl px-2 py-2 text-sm font-medium ${groupe === g.cle ? 'bg-gray-900 text-white' : 'bg-white ring-1 ring-gray-200'}`}
            >
              {g.libelle} <span className={groupe === g.cle ? 'text-gray-300' : 'text-gray-400'}>{comptes[g.cle]}</span>
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          {(choixDeClasses.classes.length > 0 || classeActive !== '') && (
            <select
              value={classeActive}
              onChange={(e) => setClasse(e.target.value)}
              aria-label="Classe"
              className={`w-2/5 min-w-0 flex-shrink-0 truncate rounded-xl border py-2 pl-3 pr-8 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500 ${
                classeActive ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-400'
              }`}
            >
              {/* Rien de choisi : « Classe » en gris, comme le texte d'aide de la recherche. */}
              <option value="" className="text-gray-900">
                {classeActive ? 'Toutes' : 'Classe'}
              </option>
              {choixDeClasses.classes.map((c) => (
                <option key={c.classe} value={c.classe} className="text-gray-900">
                  {c.classe} ({c.nombre})
                </option>
              ))}
              {choixDeClasses.sansClasse > 0 && (
                <option value={SANS_CLASSE} className="text-gray-900">
                  Sans classe ({choixDeClasses.sansClasse})
                </option>
              )}
            </select>
          )}
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Prénom ou nom…"
              className="w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* La liste : présent (✓ vert) ou rien. */}
        <ul className="mt-3 divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
          {visibles.length === 0 && (
            <li className="p-6 text-center text-sm text-gray-500">
              Aucun enfant.
              {(classeActive !== '' || recherche.trim() !== '') && (
                <button
                  type="button"
                  onClick={() => {
                    setClasse('');
                    setRecherche('');
                  }}
                  className="mt-2 block w-full rounded-xl bg-white px-3 py-2 font-medium text-gray-900 ring-1 ring-gray-200"
                >
                  Voir toute l’école
                </button>
              )}
            </li>
          )}
          {visibles.map((e) => {
            const present = plageId !== null && e.presences.includes(plageId);
            const occupe = enCours.has(e.id);
            const identite = (
              <>
                <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${present ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {initiales(e.prenom, e.nom)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {e.prenom} <span className="uppercase">{e.nom}</span>
                  </span>
                  <span className="block truncate text-xs text-gray-500">{e.classe ?? (e.groupe === 'maternelle' ? 'maternelle' : e.groupe === 'primaire' ? 'primaire' : '')}</span>
                </span>
              </>
            );
            return (
              <li key={e.id} className="flex items-center gap-3 p-2 pl-3">
                {source.lireFiche ? (
                  <button type="button" onClick={() => void ouvrirFiche(e)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    {identite}
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-3">{identite}</div>
                )}
                <button
                  type="button"
                  {...suivi}
                  onClick={() => {
                    if (plageId && geste(`${e.id}|${plageId}`)) void basculer(e);
                  }}
                  disabled={!plageId || lectureSeule || occupe || (plageBloquee && !present)}
                  aria-pressed={present}
                  aria-label={present ? `${e.prenom} présent — retirer` : `${e.prenom} — pointer présent`}
                  className={`flex h-12 w-14 flex-shrink-0 touch-manipulation select-none items-center justify-center rounded-xl text-lg font-bold transition ${
                    present ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-300'
                  } disabled:opacity-40`}
                >
                  {occupe ? <Loader2 className="h-5 w-5 animate-spin" /> : present ? <Check className="h-6 w-6" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
        {plage && (
          <p className="mt-3 text-center text-xs text-gray-500">
            {compterPresents(lecture.enfants, plage.id)} enfant(s) pointé(s) en {plage.libelle.toLowerCase()} le {dateEnClair(lecture.date)}.
          </p>
        )}
      </div>

      {fiche && <FicheDeLEnfant enfant={fiche.enfant} contenu={fiche.contenu} erreur={fiche.erreur} aujourdHui={aujourdHui} onFermer={() => setFiche(null)} />}

      {scanOuvert && scanPossible && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          }
        >
          <EcranDeScan source={source} lecture={lecture} file={file} pointePar={pointePar} onFermer={() => setScanOuvert(false)} onCodeRequis={onCodeRequis} />
        </Suspense>
      )}
    </div>
  );
}

function FicheDeLEnfant({
  enfant,
  contenu,
  erreur,
  aujourdHui,
  onFermer,
}: {
  enfant: EnfantDuLien;
  contenu: FicheGarderie | null;
  erreur: string | null;
  aujourdHui: string;
  onFermer: () => void;
}) {
  const age = contenu ? ageEnAnnees(contenu.enfant.dateNaissance, aujourdHui) : null;
  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/40 sm:items-center" onClick={onFermer} role="presentation">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">
              {enfant.prenom} <span className="uppercase">{enfant.nom}</span>
            </h2>
            <p className="text-sm text-gray-600">
              {[age !== null ? `${age} ans` : null, contenu?.enfant.classe ?? enfant.classe, contenu?.enfant.formule ?? null].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button type="button" onClick={onFermer} className="rounded-full p-2 active:bg-gray-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!contenu && !erreur && <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-primary-500" />}
        {erreur && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800">{erreur}</p>}

        {contenu && (
          <div className="mt-4 space-y-4 text-sm">
            {contenu.enfant.rentreLeMidi && <p className="rounded-xl bg-amber-50 p-3 text-amber-900">Rentre à la maison le midi.</p>}
            <section>
              <h3 className="font-semibold text-gray-900">Parents</h3>
              <ul className="mt-1 space-y-1">
                {contenu.parents.map((p, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span>
                      {p.prenom} <span className="uppercase">{p.nom}</span>
                      {contenu.parents.length > 1 && <span className="text-gray-500"> · {p.partPct} %</span>}
                    </span>
                    {p.telephone && (
                      <a href={`tel:${p.telephone.replace(/\s/g, '')}`} className="flex items-center gap-1 rounded-lg bg-primary-50 px-2 py-1 font-medium text-primary-700">
                        <Phone className="h-4 w-4" /> {p.telephone}
                      </a>
                    )}
                  </li>
                ))}
                {contenu.parents.length === 0 && <li className="text-gray-500">Aucun parent connu.</li>}
              </ul>
            </section>
            {contenu.depart && (
              <section>
                <h3 className="font-semibold text-gray-900">Départ</h3>
                <p className="mt-1">
                  {contenu.depart.partSeul ? 'Part seul·e.' : 'Ne part pas seul·e.'}
                  {contenu.depart.heure ? ` Vers ${contenu.depart.heure}.` : ''}
                </p>
                {contenu.depart.personnes.length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {contenu.depart.personnes.map((p, i) => (
                      <li key={i} className="flex items-center justify-between gap-2">
                        <span>
                          {p.prenom} {p.nom}
                          {p.lien ? <span className="text-gray-500"> · {p.lien}</span> : null}
                        </span>
                        {p.telephone && (
                          <a href={`tel:${p.telephone.replace(/\s/g, '')}`} className="flex items-center gap-1 text-primary-700">
                            <Phone className="h-4 w-4" /> {p.telephone}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            {contenu.sante && (contenu.sante.allergies || contenu.sante.regime || contenu.sante.medicaments || contenu.sante.aSavoir) && (
              <section>
                <h3 className="font-semibold text-gray-900">Santé</h3>
                <ul className="mt-1 space-y-1">
                  {contenu.sante.allergies && (
                    <li>
                      <span className="font-medium">Allergies :</span> {contenu.sante.allergiesDetails ?? 'oui'}
                      {contenu.sante.allergiesConsequences ? ` — ${contenu.sante.allergiesConsequences}` : ''}
                    </li>
                  )}
                  {contenu.sante.regime && (
                    <li>
                      <span className="font-medium">Régime :</span> {contenu.sante.regimeDetails ?? 'oui'}
                    </li>
                  )}
                  {contenu.sante.medicaments && (
                    <li>
                      <span className="font-medium">Médicaments :</span> {contenu.sante.medicamentsDetails ?? 'oui'}
                      {contenu.sante.medicamentsAutonomie ? ' (en autonomie)' : ''}
                    </li>
                  )}
                  {contenu.sante.aSavoir && (
                    <li>
                      <span className="font-medium">À savoir :</span> {contenu.sante.aSavoir}
                    </li>
                  )}
                  {contenu.sante.medecinNom && (
                    <li>
                      <span className="font-medium">Médecin :</span> {contenu.sante.medecinNom}
                      {contenu.sante.medecinTelephone ? ` · ${contenu.sante.medecinTelephone}` : ''}
                    </li>
                  )}
                </ul>
              </section>
            )}
            {!contenu.depart && !contenu.sante && <p className="text-gray-500">Pas encore de fiche détaillée : les parents la compléteront en se connectant.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
